import React, { useEffect, useState } from 'react';
import AppIcon from './icons/AppIcon';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import useRealtimeSocket from '../hooks/useRealtimeSocket';
import {
  useMarkAllNotificationsSeen,
  useMarkNotificationSeen,
  useNotifications,
} from '../hooks/useNotificationsQuery';
import { queryKeys } from '../query/queryKeys';

/**
 * Architecture Rule 76 v1.101 — per-notification-type side-effects when a
 * NOTIFICATION_NEW arrives on the WS.
 *
 * Returns either `null` (no side-effect — handler just updates the bell
 * badge + fires the standard toast) or `{ invalidatePrefix, toastIcon }`
 * so NotificationBell can run a single consolidated invalidation + a
 * single type-aware toast emoji.
 *
 * Keep this map small and explicit — high-priority module-level events
 * (money in/out, friend mutual, etc.) opt in here; everything else stays
 * generic and lands in the bell list only.
 */
function notificationTypeSideEffects(type) {
  if (!type) return null;
  if (type.startsWith('bloom_withdrawal_')) {
    return {
      invalidatePrefix: queryKeys.bloomGifts?.walletPrefix,
      toastIcon: type === 'bloom_withdrawal_completed' ? '🎉'
              : type === 'bloom_withdrawal_rejected'  ? '⚠️'
              : '⏳',
      toastVariant: type === 'bloom_withdrawal_completed' ? 'success'
                 : type === 'bloom_withdrawal_rejected'  ? 'error'
                 : 'default',
    };
  }
  return null;
}

const NOTIFICATION_PARAMS = { page: 1, per_page: 20 };

function mergeRealtimeNotification(data, notification) {
  if (!notification?.id) return data;

  const items = data?.items ?? [];
  if (items.some((item) => Number(item.id) === Number(notification.id))) {
    return data;
  }

  const perPage = Number(data?.pagination?.per_page ?? NOTIFICATION_PARAMS.per_page);
  return {
    ...(data ?? {}),
    items: [notification, ...items].slice(0, perPage),
    unread: Number(data?.unread ?? 0) + (notification.seen_at ? 0 : 1),
    pagination: data?.pagination
      ? { ...data.pagination, total: Number(data.pagination.total ?? 0) + 1 }
      : data?.pagination,
  };
}

export default function NotificationBell() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { subscribeAll } = useRealtimeSocket();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useNotifications(NOTIFICATION_PARAMS);
  const markAllSeen = useMarkAllNotificationsSeen();
  const markSeen = useMarkNotificationSeen();

  const items = data?.items ?? [];
  const unread = Number(data?.unread ?? 0);
  const menuId = 'notifications-menu';

  useEffect(() => {
    return subscribeAll((event) => {
      // v1.101 — `NOTIFICATION_NEW` is the canonical event for new bell
      // entries. We also accept the legacy `NOTIFICATION` + `CHAT_NOTIFICATION`
      // types so older publishers (and chat fan-out) keep working without
      // a coordinated backend change.
      const isNotif = event?.type === 'NOTIFICATION_NEW'
                   || event?.type === 'NOTIFICATION'
                   || event?.type === 'CHAT_NOTIFICATION';
      if (!isNotif) return;

      const notification = event.notification ?? (event.id ? event : null);
      if (notification) {
        queryClient.setQueriesData(
          { queryKey: queryKeys.notifications.all },
          (oldData) => mergeRealtimeNotification(oldData, notification),
        );
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });

      // Per-type side effects + the SINGLE toast for new notifications.
      // Chat messages are silenced here (useChatNotificationSound +
      // ChatPage's per-thread merge handle them with their own UX);
      // everything else gets a brief toast so the user has an immediate
      // signal without having to look at the bell.
      if (event?.type === 'CHAT_NOTIFICATION') return;
      if (!notification) return;

      const sideEffects = notificationTypeSideEffects(notification.type);
      if (sideEffects?.invalidatePrefix) {
        queryClient.invalidateQueries({ queryKey: sideEffects.invalidatePrefix });
      }

      const title = notification.title || t('notifications.newGeneric');
      const body  = notification.body  || '';
      const url   = notification.url   || null;
      const icon  = sideEffects?.toastIcon ?? '🔔';
      const variant = sideEffects?.toastVariant ?? 'default';

      const renderToast = (toastInstance) => (
        <span
          role={url ? 'button' : undefined}
          tabIndex={url ? 0 : undefined}
          onClick={() => {
            if (url) navigate(url);
            toast.dismiss(toastInstance.id);
          }}
          onKeyDown={(e) => {
            if (!url) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(url);
              toast.dismiss(toastInstance.id);
            }
          }}
          style={{ cursor: url ? 'pointer' : 'default', display: 'block' }}
        >
          <strong>{title}</strong>
          {body && (
            <>
              <br />
              <span style={{ fontSize: '0.85em' }}>{body}</span>
            </>
          )}
        </span>
      );

      const opts = { duration: variant === 'default' ? 6000 : 9000, icon };
      if (variant === 'success')      toast.success(renderToast, opts);
      else if (variant === 'error')   toast.error(renderToast, opts);
      else                            toast(renderToast, opts);
    });
  }, [queryClient, subscribeAll, navigate, t]);

  const handleMarkAll = async () => {
    try {
      await markAllSeen.mutateAsync();
    } catch (_) {
      // The global API layer normalizes errors; notification refresh should stay non-blocking.
    }
  };

  const handleClickItem = async (notification) => {
    try {
      if (!notification.seen_at) {
        await markSeen.mutateAsync(notification.id);
      }
    } catch (_) {
      // Navigation should still work when a seen-status update fails.
    }

    if (notification.url) {
      window.location.href = notification.url;
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="relative rounded-full p-2 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
        aria-label={t('notifications.label')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
      >
        <AppIcon name="notification" size={22} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={t('notifications.title')}
          className="absolute right-0 z-50 mt-2 max-h-[28rem] w-80 overflow-y-auto rounded-2xl border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-lg)]"
        >
          <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-2">
            <h3 className="text-sm font-semibold page-title">{t('notifications.title')}</h3>
            <button
              type="button"
              onClick={handleMarkAll}
              disabled={markAllSeen.isPending || unread === 0}
              className="text-xs text-[var(--accent-strong)] hover:underline disabled:text-[var(--muted)]"
            >
              {t('notifications.markAllSeen')}
            </button>
          </div>

          {isLoading && (
            <p className="px-4 py-6 text-center text-sm page-subtitle">{t('app.loading')}</p>
          )}

          {!isLoading && items.length === 0 && (
            <p className="px-4 py-6 text-center text-sm page-subtitle">
              {t('notifications.none')}
            </p>
          )}

          {!isLoading && items.map((notification) => (
            <button
              key={notification.id}
              type="button"
              role="menuitem"
              onClick={() => handleClickItem(notification)}
              className={`block w-full border-b border-[var(--border)] px-4 py-3 text-left text-sm hover:bg-[var(--bg-muted)] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--accent)] ${notification.seen_at ? '' : 'bg-[var(--accent-soft)]'}`}
            >
              <p className="font-semibold page-title">{notification.title}</p>
              {notification.body && <p className="text-xs page-subtitle">{notification.body}</p>}
              <p className="mt-1 text-[10px] page-subtitle">{notification.created_at}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
